import type { ReactNode } from "react";
import { Pressable, StyleSheet, Text } from "react-native";
import { tabBar } from "@heraion/shared";
import { stateAria } from "../a11y";

// One segment of the experience switcher row: icon over a small label, sized
// from the shared `tabBar` token so it matches the bottom-tab footer 1:1 (icon
// box, label size, active-weight cue, tap height). The ⚙︎ and the bell are
// segments like the experiences, so they all render through this one component
// rather than through a second copy of the same Pressable. The tint is derived
// here so an active segment can never end up carrying colour without weight
// (a11y: never colour alone).
export type SegmentIcon = (props: { color: string; size: number }) => ReactNode;

export type SegmentProps = {
  selected?: boolean;
  icon: SegmentIcon;
  label: string;
  onPress: () => void;
  palette: { brand500: string; brandText: string; ink3: string };
  /** `tab` for a mode; `button` for an action such as the bell. */
  role?: "tab" | "button";
  /** Spoken name when it differs from the visible label (the bell's count). */
  accessibilityLabel?: string;
  /** Drawn over the icon (the bell's counter). Receives the icon size. */
  renderIcon?: (icon: ReactNode, size: number) => ReactNode;
};

export function Segment({
  selected = false,
  icon: Icon,
  label,
  onPress,
  palette,
  role = "tab",
  accessibilityLabel,
  renderIcon,
}: SegmentProps) {
  const tint = selected ? palette.brand500 : palette.ink3;
  // The label is 10px text: brandText is the brand hue gated at 4.5:1 on
  // surface in both themes; brand500 (the icon) is not.
  const labelTint = selected ? palette.brandText : palette.ink3;
  const icon = <Icon color={tint} size={tabBar.iconSize} />;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole={role}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={role === "tab" ? { selected } : undefined}
      {...(role === "tab" ? stateAria({ selected }, "tab") : {})}
      style={styles.tab}
    >
      {renderIcon ? renderIcon(icon, tabBar.iconSize) : icon}
      <Text
        numberOfLines={1}
        // A fixed-width nav row: cap the growth so the word cannot outgrow it.
        maxFontSizeMultiplier={1.3}
        style={[styles.label, { color: labelTint }, selected && styles.labelActive]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tab: {
    flex: 1,
    minHeight: tabBar.itemMinHeight,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 6,
    gap: 2,
  },
  label: { fontSize: tabBar.labelSize, fontWeight: tabBar.labelWeight },
  labelActive: { fontWeight: tabBar.labelWeightActive },
});
